import React, { useEffect, useMemo, useState, useRef, useCallback, KeyboardEvent, ChangeEvent } from "react";
import { useLocation } from "react-router-dom";
import { Link } from "react-router-dom";
import { Modal, Button, Form } from "react-bootstrap";
import { showSuccess, showError } from "toast/toast";
import { useNavigate, useParams } from "react-router-dom";
import { Product } from "types";
import { Row } from "@tanstack/react-table";
import DataTable from "components/DataTable";
import PaginationControls from "components/PaginationControls";
import type { Table } from "@tanstack/react-table";
import { Warehouse } from "types";
import { StatusBadge } from "components/Productlocationstatus";
import TablePaginationControls from "components/TableandmobilePaginationcontrol"
import { DesktopTableParts, MobileCardsOnly } from "components/TableandMobilecomponent";
import {
  DesktopTableLoader,
  MobileCardsLoader,
  DataTableStatus,
} from "components/DataTableLoader";
import { getImageSrc, useVariantImageUrlMap } from "components/Tableimageshelpers";
import imageCustomSquare from "assets/images/imagecustomrounded.png";
import BinLocationPopUp from "components/BinLocationPopUp";
import { sendTriggerEmails } from "utils/emailService";
import { fetchUserData, fetchCustomerGroupByDomain, fetchParentHierarchy } from "utils/userDataFetcher";

import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  ColumnDef,
  getPaginationRowModel,
  flexRender,
  createColumnHelper,
  TableMeta,
} from "@tanstack/react-table";

import {
  ExportAsExcel,
} from "@siamf/react-export";

import { TableHead, TableRow, TableCell, TableBody } from '@mui/material';
import { ViewIconButton } from "components/DatatablesActionIcons";
import { getAuthCookie } from "../../utils/authCookies";
import { useModuleActionPermissions } from "hooks/useModuleActionPermissions";
import { MODULE_PERMISSION_NAMES } from "utils/moduleActionPermissions";

interface MyTableMeta {
  noteSelections: Record<string, string>;
  binLocationSelections: Record<string, string>;
  rotationProduct: Record<string, string>;
  warehouseSelections: Record<string, string>;
  supplierActionSelections: Record<string, string>;
  handleNoteChange: (productId: string, value: string) => void;
  handleBinLocationChange: (productId: string, value: string) => void;
  handleRotationalProduct: (productId: string, value: string) => void;
  handleWarehouseChange: (productId: string, value: string) => void;
  handleSupplierActionChange: (productId: string, value: string) => void;
  handleSupplierAction: (productId: string, agentId: string) => void;
  handleUnlink: (productId: string, agentId: string) => void;
  warehouses: Warehouse[];
  zoneSelections: Record<string, string>;
  rockSelections: Record<string, string>;
  handleZoneChange: (productId: string, value: string) => void;
  handleRockChange: (productId: string, value: string) => void;
  warehouseData: Record<string, WarehouseHierarchy>;
  selectedLocations: Record<string, Array<{
    quantity: number;
    warehouse_id: string; warehouse_name: string;
    zone_id: string; zone_name: string;
    rack_id: string; rack_name: string;
    bin_id: string; bin_name: string;
  }>>;
}


interface ExportRow {
  "First Name": string;
  "Last Name": string;
  "Email": string;
  "Phone": string;
  "Brokerage Firm Affiliated": string;
  "Office": string;
  "Address": string;
  "License Status": string;
  "Status": string;
}

function fuzzyTextFilter<TData>(
  row: Row<TData>,
  columnId: string,
  filterValue: string
) {
  const raw = row.getValue(columnId);
  const value = (typeof raw === "boolean"
    ? raw
      ? "Active"
      : "Inactive"
    : String(raw)
  ).toLowerCase();

  return value.includes(filterValue.toLowerCase());
}

type Dimensions = {
  length: number;
  height: number;
  width: number;
  weight: number;
};

type Image = {
  url: string;
};

type AgentProduct = {
  agent_name: string;
  agent_email: string;
  product_id: string;
  agent_id: string;
  product: Product;
  inventory_type: string;
  created_by: { id: string; name: string };
  owner_node_type?: string;
  owner_node_id?: string;
};

type LinkedInstaller = {
  id: string;
  name: string;
  requested_quantity: number;
  warehouse_info: {
    id: string;
    label: string;
    bin_location: string;
    linked_product_quantity: number;
  };
};

type WarehouseHierarchy = {
  zones: { _id: string, label: string }[],
  racks: { _id: string, label: string }[],
  bins: { _id: string, label: string, max_volume?: number, used_volume?: number }[],
  selectedZone?: { _id: string, label: string },
  selectedRack?: { _id: string, label: string },
  selectedBin?: { _id: string, label: string }
};


// type Warehouse = {
//   id: string;
//   label: string;
//   status: string;
// };

type SelectionState = {
  [productId: string]: string;
};

const columnHelper = createColumnHelper<AgentProduct>();
const HEX24 = /^[a-f0-9]{24}$/i;
const ABS_URL = /^https?:\/\//i;
const isHexId = (s?: string) => !!(s && HEX24.test(s.trim()));

const SignageIncomingAgentProducts = () => {
  const [agentProducts, setAgentProducts] = useState<AgentProduct[]>([]);
  const [error, setError] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState<string | null>(null); // Track deletion in progress
  const token = getAuthCookie("sessionToken");
  const userId = getAuthCookie("userId");
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<AgentProduct | null>(
    null
  );
  const [selectedWarehouse, setSelectedWarehouse] = useState("");
  const [binLocation, setBinLocation] = useState("");
  const [toastShown, setToastShown] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  // Determine the base route based on current location
  const isSignageTeammember = location.pathname.includes('/signageteammember');
  const baseRoute = isSignageTeammember ? '/signageteammember' : '/signage';

  const { canCreate, canView, canEdit, canDelete } = useModuleActionPermissions(
    MODULE_PERMISSION_NAMES.agent_incoming_products
  );

  const [parentSignageId, setParentSignageId] = useState<string | null>(null);
  const [parentSignageName, setParentSignageName] = useState<string | null>(null);

  const [supplierActionSelections, setSupplierActionSelections] = useState<SelectionState>({});
  const [warehouseSelections, setWarehouseSelections] = useState<SelectionState>({});
  const [zoneSelections, setZoneSelections] = useState<SelectionState>({});
  const [rockSelections, setRockSelections] = useState<SelectionState>({});
  const [binLocationSelections, setBinLocationSelections] = useState<SelectionState>({});
  const [noteSelections, setNoteSelections] = useState<SelectionState>({});
  const [rotationProduct, setRotationProduct] = useState<SelectionState>({});
  const [linkedStatusFilter, setLinkedStatusFilter] = useState("");
  const [currentStatusFilter, setCurrentStatusFilter] = useState("");
  const [customStartDate, setCustomStartDate] = useState("");
  const [customEndDate, setCustomEndDate] = useState("");
  const itemsPerPage = 10;
  const [rowCount, setRowCount] = useState(0);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });
  const [globalFilter, setGlobalFilter] = useState("");
  const [exportRows, setExportRows] = useState<ExportRow[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchInput, setSearchInput] = useState<string>(globalFilter ?? "");
  const topRef = useRef<HTMLDivElement>(null);
  const confirmedProductsRef = useRef<Set<string>>(new Set()); // Track products that were confirmed in current session

  const [warehouseData, setWarehouseData] = useState<Record<string, WarehouseHierarchy>>({});
  const [tempLinkedStatus, setTempLinkedStatus] = useState("all"); // UI dropdown
  const [tempCurrentStatus, setTempCurrentStatus] = useState("all"); // UI dropdown
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [showBinLocationPopup, setShowBinLocationPopup] = useState<Record<string, boolean>>({});
  const [selectedLocations, setSelectedLocations] = useState<Record<string, Array<{
    quantity: number;
    warehouse_id: string; warehouse_name: string;
    zone_id: string; zone_name: string;
    rack_id: string; rack_name: string;
    bin_id: string; bin_name: string;
  }>>>({});

  const allImageIds = useMemo(() => {
    const ids: string[] = [];
    for (const row of (agentProducts || [])) {
      const id = (row?.product as any)?.images?.[0]?.url as string;
      if (isHexId(id)) ids.push(id.trim());
    }
    return Array.from(new Set(ids));
  }, [agentProducts]);

  // hook expects: [{ product: { variants: [{ images: [{ url }] }] } }]
  const dataForMap = useMemo(() => {
    if (allImageIds.length === 0) return [];
    return [
      { product: { variants: [{ images: allImageIds.map(url => ({ url })) }] } }
    ];
  }, [allImageIds]);

  const imageUrlMap = useVariantImageUrlMap(dataForMap, token);


  // Fetch products
  const fetchProducts = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/signage/agent/incoming-products/datatable`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            page: pagination.pageIndex + 1,
            limit: pagination.pageSize,
            search: globalFilter,
            filters: {
              locked_status: linkedStatusFilter,
              current_status: currentStatusFilter,
            },
            sort: {
              field: "created_at",
              order: "desc",
            },
          }),
        }
      );
      const result = await response.json();
      setAgentProducts(result?.data ?? []);
      setRowCount(result?.total ?? 0);
      // console.log("agentProducts", result?.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "An unknown error occurred");
    } finally {
      setLoading(false);
    }
  };



  // Fetch parent hierarchy if user is a signage team member
  useEffect(() => {
    const fetchParentHierarchyData = async () => {
      if (isSignageTeammember && token) {
        const hierarchyData = await fetchParentHierarchy(token);
        if (hierarchyData) {
          setParentSignageId(hierarchyData.parentSignageId);
          setParentSignageName(hierarchyData.parentSignageName);
        }
      }
    };

    fetchParentHierarchyData();
  }, [isSignageTeammember, token]);

  useEffect(() => {
    if (token) fetchProducts();
  }, [token, globalFilter, pagination, linkedStatusFilter, currentStatusFilter]);



  // Fetch warehouses
  useEffect(() => {
    const fetchWarehouses = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/warehouses`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });
        const data = await response.json();
        // console.log("warehouses", data);
        setWarehouses(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : "An unknown error occurred");
      }
    };

    if (token) fetchWarehouses();
  }, [token]);

  const handleAcceptClick = (product: AgentProduct) => {
    setSelectedProduct(product);
    setShowModal(true);
  };

  //   if (loading) {
  //     return <div>Loading...</div>;
  //   }

  async function fetchHierarchy(payload: object) {
    const res = await fetch(`${process.env.REACT_APP_API_URL}/warehouses/hierarchy`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    });
    if (!res.ok) throw new Error("Failed to fetch hierarchy");
    return res.json();
  }


  const handleSupplierActionChange = (productId: string, value: string) => {
    // console.log('Selected supplier action:', value);
    setSupplierActionSelections((prev) => ({
      ...prev,
      [productId]: value,
    }));
    // Automatically open popup when "with_supplier" is selected
    if (value === "with_supplier") {
      setShowBinLocationPopup(prev => ({ ...prev, [productId]: true }));
    }
  };

  const handleWarehouseChange = useCallback(async (productId: string, warehouseId: string) => {
    setWarehouseSelections(prev => ({ ...prev, [productId]: warehouseId }));
    setZoneSelections(prev => ({ ...prev, [productId]: "" }));
    setRockSelections(prev => ({ ...prev, [productId]: "" }));
    setBinLocationSelections(prev => ({ ...prev, [productId]: "" }));
    if (warehouseId) {
      const result = await fetchHierarchy({ warehouse_id: warehouseId });
      // console.log("result", result);
      setWarehouseData(prev => ({
        ...prev,
        [productId]: {
          ...prev[productId],
          zones: result.zones || [],
          racks: [],
          bins: [],
          selectedZone: undefined,
          selectedRack: undefined,
          selectedBin: undefined,
        }
      }));
    }
  }, []);

  const handleZoneChange = async (productId: string, zoneId: string) => {
    setZoneSelections(prev => ({ ...prev, [productId]: zoneId }));
    setRockSelections(prev => ({ ...prev, [productId]: "" }));
    setBinLocationSelections(prev => ({ ...prev, [productId]: "" }));

    const zoneObj = warehouseData[productId]?.zones?.find(z => z._id === zoneId);
    if (zoneId) {
      const result = await fetchHierarchy({ zone_id: zoneId });
      setWarehouseData(prev => ({
        ...prev,
        [productId]: {
          ...prev[productId],
          racks: result.racks || [],
          bins: [],
          selectedZone: zoneObj,
          selectedRack: undefined,
          selectedBin: undefined,
        }
      }));
    }
  };


  const handleRockChange = async (productId: string, rackId: string) => {
    setRockSelections(prev => ({ ...prev, [productId]: rackId }));
    setBinLocationSelections(prev => ({ ...prev, [productId]: "" }));

    const rackObj = warehouseData[productId]?.racks?.find(r => r._id === rackId);
    if (rackId) {
      const result = await fetchHierarchy({ rack_id: rackId });
      setWarehouseData(prev => ({
        ...prev,
        [productId]: {
          ...prev[productId],
          bins: result.bins || [],
          selectedRack: rackObj,
          selectedBin: undefined,
        }
      }));
    }
  };


  const handleBinLocationChange = (productId: string, binId: string) => {
    setBinLocationSelections(prev => ({ ...prev, [productId]: binId }));
    const binObj = warehouseData[productId]?.bins?.find(b => b._id === binId);
    setWarehouseData(prev => ({
      ...prev,
      [productId]: {
        ...prev[productId],
        selectedBin: binObj
      }
    }));
  };



  const handleNoteChange = useCallback((productId: string, value: string) => {
    setNoteSelections((prev) => ({
      ...prev,
      [productId]: value,
    }));
  }, []);


  const handleRotationalProduct = (productId: string, value: string) => {
    setRotationProduct((prev) => ({
      ...prev,
      [productId]: value,
    }));
  };

  // useEffect(() => {
  //   console.log("warehouseData", warehouseData);
  // }),[warehouseData]

  const handleSupplierAction = async (productId: string, agentId: string) => {
    const action = supplierActionSelections[productId];
    const agentProduct: AgentProduct | undefined = agentProducts.find(p => p.product_id === productId);

    const actionEndpoints: Record<string, string> = {
      in_transit: "in-transit",
      on_site: "on-site",
      declined: "decline-after-pickup",
    };

    const endpointSuffix = actionEndpoints[action] || "accept";
    const endpoint = `${process.env.REACT_APP_API_URL}/linked-installer/agent/${agentId}/product/${productId}/${endpointSuffix}`;

    const noteOrReason = noteSelections[productId] || "";
    const locations = selectedLocations[productId] || [];
    const totalQuantity = locations.reduce((sum, loc) => sum + (loc.quantity || 0), 0);

    const quantity = agentProduct?.product.variants?.[0]?.inventory?.[0]?.available || 0;
    const product_location_status = agentProduct?.product.product_location_status || "";
    const assigned_at = new Date().toISOString();
    const body =
      action === "declined"
        ? { reason: noteOrReason }
        : ["in_transit", "on_site"].includes(action)
          ? {
            note: noteOrReason,
            storage_locations: [
              {
                quantity: totalQuantity || quantity,
                product_location_status,
                current_status: action || "",
                location: [],
                assigned_at,
              }
            ]
          }
          : {
            storage_locations: [
              {
                quantity: totalQuantity || quantity,
                product_location_status,
                current_status: action || "",
                location: locations.map(loc => ({
                  quantity: loc.quantity,
                  warehouse_id: loc.warehouse_id,
                  warehouse_name: loc.warehouse_name,
                  zone_id: loc.zone_id,
                  zone_name: loc.zone_name,
                  rack_id: loc.rack_id,
                  rack_name: loc.rack_name,
                  bin_id: loc.bin_id,
                  bin_name: loc.bin_name,
                })),
                assigned_at,
              }
            ],
            is_rotational: false,
            note: noteOrReason,
          };

    try {
      const response = await fetch(endpoint, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(body),
      });
      const result: any = await response.json();
      if (!response.ok || result?.error) {
        const message = result?.error || result?.message || "Failed to save data";
        console.error("API Error:", message);
        showError(message);
        throw new Error(message);
      }
      showSuccess(result.message || "Status updated successfully");

      // Send notifications to signage team members, userId, and agentId when product link is accepted
      if (action === "with_supplier" || endpointSuffix === "accept") {
        try {
          const { createNotification } = await import("../../services/notificationApi");
          const { getProductLinkRequestedSignageContent } = await import("../../services/notificationContent");
          const productName = agentProduct?.product?.name || "Product";
          
          // Get agent name for the requester name
          const requesterName = agentProduct?.agent_name || "Agent";
          const agentId = agentProduct?.agent_id;
          
          // Get effective signage ID (parent if team member, otherwise userId)
          const effectiveSignageId = (isSignageTeammember && parentSignageId ? parentSignageId : userId) || "";
          
          // 1. Send notification to userId (the signage user performing the action)
          if (userId && token) {
            try {
              const currentUserData = await fetchUserData(userId, token);
              const userLicenseType = currentUserData?.licenseType || "SIGNAGE_TEAM_MEMBER";
              const userRecipientType = userLicenseType === "SIGNAGE_FIRM" ? "signage_admin" : "team_member";
              const content = getProductLinkRequestedSignageContent({
                agentOrTeamMemberName: requesterName,
                productName: productName,
                recipientType: userRecipientType,
              });
              await createNotification(userId, content, token, userRecipientType);
            } catch (userErr) {
              console.error(`Failed to send notification to userId ${userId}:`, userErr);
            }
          }
          
          // 2. Send notifications to all signage team members
          if (effectiveSignageId && token) {
            try {
              const signageTeamResp = await fetch(
                `${process.env.REACT_APP_API_URL}/users/by-supplier/${effectiveSignageId}`,
                {
                  method: "GET",
                  headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                  },
                }
              );

              if (signageTeamResp.ok) {
                const signageTeamData = await signageTeamResp.json();
                const signageTeamMembers = Array.isArray(signageTeamData?.data) ? signageTeamData.data : [];

                // Send notifications to all signage admin/team members (excluding userId to avoid duplicate)
                for (const member of signageTeamMembers) {
                  const memberId = member.id || member._id;
                  if (memberId && memberId !== userId && token) {
                    try {
                      const memberLicenseType = member.licenseType || "SIGNAGE_TEAM_MEMBER";
                      const recipientType = memberLicenseType === "SIGNAGE_FIRM" ? "signage_admin" : "team_member";
                      const content = getProductLinkRequestedSignageContent({
                        agentOrTeamMemberName: requesterName,
                        productName: productName,
                        recipientType: recipientType,
                      });
                      await createNotification(memberId, content, token, recipientType);
                    } catch (memberErr) {
                      console.error(`Failed to send notification to signage team member ${memberId}:`, memberErr);
                    }
                  }
                }
              }
            } catch (signageErr) {
              console.error("Failed to fetch signage team members for notification:", signageErr);
            }
          }
          
          // 3. Send notification to agentId (the agent who requested the link)
          if (agentId && token) {
            try {
              const content = getProductLinkRequestedSignageContent({
                agentOrTeamMemberName: requesterName,
                productName: productName,
                recipientType: "agent",
              });
              await createNotification(agentId, content, token, "agent");
            } catch (agentErr) {
              console.error(`Failed to send notification to agentId ${agentId}:`, agentErr);
            }
          }
        } catch (notifError) {
          console.error("Failed to send product link requested notifications:", notifError);
        }
      }

      // Send email notifications
      try {
        // Determine trigger name based on action
        const triggerName = action === "declined" ? "Decline" : "Product link requested";
        
        // Get product details
        const productName = agentProduct?.product?.name || "";
        const sku = agentProduct?.product?.sku || "";
        const ownerNodeType = (agentProduct as any)?.owner_node_type || "";
        const ownerNodeId = (agentProduct as any)?.owner_node_id || "";
        const supplierName = userId ? await fetchUserData(userId, token).then(u => u?.firstName || u?.email?.split("@")[0] || "Supplier") : "Supplier";
        const declinedBy = supplierName;
        const reason = noteOrReason || "No reason provided";
        const declinedOn = new Date().toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
        const updatedOn = declinedOn;
        const newStatus = action === "declined" ? "Declined" : action === "in_transit" ? "In Transit" : action === "on_site" ? "On Site" : action === "with_supplier" ? "With Supplier" : "Accepted";
        const supplierComment = noteOrReason || "";

        // Collect recipient emails
        const recipientEmails: string[] = [];
        const licenseTypes: string[] = [];

        // Get customer group users by domain "warehouse"
        // Use parentSignageId if user is a team member, otherwise use userId
        const effectiveSignageId = (isSignageTeammember && parentSignageId ? parentSignageId : userId) || "";
        const customerGroup = await fetchCustomerGroupByDomain(effectiveSignageId, "warehouse", token);
        if (customerGroup?.allowed_user_ids) {
          customerGroup.allowed_user_ids.forEach((user: any) => {
            if (user?.email && user.email.trim()) {
              const email = user.email.trim();
              if (!recipientEmails.includes(email)) {
                recipientEmails.push(email);
                licenseTypes.push("SIGNAGE_TEAM_MEMBER"); // Assuming warehouse managers are team members
              }
            }
          });
        }

        // If owner_node_type is "brokerage", fetch and add brokerage email
        // if (ownerNodeType?.toLowerCase() === "brokerage" && ownerNodeId) {
        //   const brokerageUser = await fetchUserData(ownerNodeId, token);
        //   if (brokerageUser?.email) {
        //     const email = brokerageUser.email.trim();
        //     if (!recipientEmails.includes(email)) {
        //       recipientEmails.push(email);
        //       licenseTypes.push("BROKERAGE_FIRM");
        //     }
        //   }
        // }

        // Send emails if there are recipients
        if (recipientEmails.length > 0) {
          const from = process.env.REACT_APP_FROM_EMAIL2 || "";
          const emailParams: Record<string, string> = {
            productName: productName,
            sku: sku,
            supplierName: supplierName,
            declinedBy: declinedBy,
            reason: reason,
            declinedOn: declinedOn,
            updatedOn: updatedOn,
            newStatus: newStatus,
            supplierComment: supplierComment,
            firstName: "", // Will be replaced in email template
          };

          await sendTriggerEmails(
            triggerName,
            licenseTypes,
            emailParams,
            recipientEmails,
            from,
            token || ""
          );
          console.log(`Email notifications sent for ${triggerName}`);
        }
      } catch (emailError) {
        console.error("Failed to send email notifications:", emailError);
        // Don't fail the entire operation if email fails
      }

      navigate(0); // refreshes the page
    } catch (error) {
      console.error("Error updating product:", error);
    }
  };

  const handleUnlink = async (productId: string, agentId: string) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/linked-installer/agent/${agentId}/product/${productId}/approve-unlink`,
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const result: any = await response.json();
      if (!response.ok || result?.error) {
        const message = result?.error || result?.message || "Failed to save data";
        console.error("API Error:", message);
        showError(message);
        throw new Error(message);
      }
      showSuccess(result.message);

      // Send notifications to signage team members, userId, and agentId when product is unlinked
      try {
        const { createNotification } = await import("../../services/notificationApi");
        const { getProductUnlinkedSignageContent } = await import("../../services/notificationContent");
        const agentProduct = agentProducts.find(
          p => p.product_id === productId && p.agent_id === agentId
        );
        const productName = agentProduct?.product?.name || "Product";
        
        // Get agent name for the requester name
        const requesterName = agentProduct?.agent_name || "Agent";
        
        // Get effective signage ID (parent if team member, otherwise userId)
        const effectiveSignageId = (isSignageTeammember && parentSignageId ? parentSignageId : userId) || "";
        
        // 1. Send notification to userId (the signage user performing the action)
        if (userId && token) {
          try {
            const currentUserData = await fetchUserData(userId, token);
            const userLicenseType = currentUserData?.licenseType || "SIGNAGE_TEAM_MEMBER";
            const userRecipientType = userLicenseType === "SIGNAGE_FIRM" ? "signage_admin" : "team_member";
            const content = getProductUnlinkedSignageContent({
              agentOrTeamMemberName: requesterName,
              productName: productName,
              recipientType: userRecipientType,
            });
            await createNotification(userId, content, token, userRecipientType);
          } catch (userErr) {
            console.error(`Failed to send notification to userId ${userId}:`, userErr);
          }
        }
        
        // 2. Send notifications to all signage team members
        if (effectiveSignageId && token) {
          try {
            const signageTeamResp = await fetch(
              `${process.env.REACT_APP_API_URL}/users/by-supplier/${effectiveSignageId}`,
              {
                method: "GET",
                headers: {
                  Authorization: `Bearer ${token}`,
                  "Content-Type": "application/json",
                },
              }
            );

            if (signageTeamResp.ok) {
              const signageTeamData = await signageTeamResp.json();
              const signageTeamMembers = Array.isArray(signageTeamData?.data) ? signageTeamData.data : [];

              // Send notifications to all signage admin/team members (excluding userId to avoid duplicate)
              for (const member of signageTeamMembers) {
                const memberId = member.id || member._id;
                if (memberId && memberId !== userId && token) {
                  try {
                    const memberLicenseType = member.licenseType || "SIGNAGE_TEAM_MEMBER";
                    const recipientType = memberLicenseType === "SIGNAGE_FIRM" ? "signage_admin" : "team_member";
                    const content = getProductUnlinkedSignageContent({
                      agentOrTeamMemberName: requesterName,
                      productName: productName,
                      recipientType: recipientType,
                    });
                    await createNotification(memberId, content, token, recipientType);
                  } catch (memberErr) {
                    console.error(`Failed to send notification to signage team member ${memberId}:`, memberErr);
                  }
                }
              }
            }
          } catch (signageErr) {
            console.error("Failed to fetch signage team members for notification:", signageErr);
          }
        }
        
        // 3. Send notification to agentId (the agent who requested the unlink)
        if (agentId && token) {
          try {
            const content = getProductUnlinkedSignageContent({
              agentOrTeamMemberName: requesterName,
              productName: productName,
              recipientType: "agent",
            });
            await createNotification(agentId, content, token, "agent");
          } catch (agentErr) {
            console.error(`Failed to send notification to agentId ${agentId}:`, agentErr);
          }
        }
      } catch (notifError) {
        console.error("Failed to send product unlinked notifications:", notifError);
      }

      navigate(0);
    } catch (error) {
      console.error("Error Unlink product link:", error);
    }
  };

  interface SupplierActionsCellProps {
    agentProduct: AgentProduct;
    note: string;
    binLocation: string;
    onBinChange: (productId: string, value: string) => void;
    onNoteChange: (productId: string, value: string) => void;
    rotationProduct: SelectionState;
    warehouseSelections: SelectionState;
    supplierActionSelections: SelectionState;
    handleRotationalProduct: (productId: string, value: string) => void;
    handleWarehouseChange: (productId: string, value: string) => void;
    handleSupplierActionChange: (productId: string, value: string) => void;
    handleSupplierAction: (productId: string, agentId: string) => void;
    handleUnlink: (productId: string, agentId: string) => void;
    warehouses: Warehouse[];
    zoneSelections: SelectionState;
    rockSelections: SelectionState;
    handleZoneChange: (productId: string, value: string) => void;
    handleRockChange: (productId: string, value: string) => void;
    warehouseData: Record<string, WarehouseHierarchy>;
    binLocationSelections: SelectionState;
    selectedLocations: Record<string, Array<{
      quantity: number;
      warehouse_id: string; warehouse_name: string;
      zone_id: string; zone_name: string;
      rack_id: string; rack_name: string;
      bin_id: string; bin_name: string;
    }>>;
  }

  const SupplierActionsCell: React.FC<SupplierActionsCellProps> = ({
    agentProduct,
    note,
    onNoteChange,
    onBinChange,
    rotationProduct,
    warehouseSelections,
    supplierActionSelections,
    handleRotationalProduct,
    handleWarehouseChange,
    handleSupplierActionChange,
    handleSupplierAction,
    handleUnlink,
    warehouses,
    zoneSelections,
    rockSelections,
    handleZoneChange,
    handleRockChange,
    warehouseData,
    binLocationSelections,
    selectedLocations,
  }) => {
    const productId = agentProduct.product_id;
    const currentStatus = agentProduct.product?.current_status || "";
    const locked = agentProduct.product?.locked_status === "locked";
    const linkedMode = agentProduct.product?.linked_mode || "";

    const supplierActionValue = supplierActionSelections[productId] ?? "";
    const hasResetRef = useRef(false);
    // Reset dependent selects when switching away from "with_supplier"
    useEffect(() => {
      if (supplierActionValue !== "with_supplier" && !hasResetRef.current) {
        handleWarehouseChange(productId, "");
        handleZoneChange(productId, "");
        handleRockChange(productId, "");
        onBinChange(productId, "");
        handleRotationalProduct(productId, "");
        hasResetRef.current = true; // prevent running again
      }

      // Reset the ref when switching back to "with_supplier"
      if (supplierActionValue === "with_supplier") {
        hasResetRef.current = false;
      }
    }, [supplierActionValue]);

    const warehouseSelected = warehouseSelections[productId] ?? "";
    const zoneSelected = zoneSelections[productId] ?? "";
    const rackSelected = rockSelections[productId] ?? "";
    const binSelected = binLocationSelections[productId] ?? "";
    const rotationalSelected = rotationProduct[productId] ?? "";

    // Confirm button enable logic
    const canConfirmSimple =
      (currentStatus === "awaiting_response" || currentStatus === "in_transit") &&
      supplierActionValue !== "";

    const canConfirmPendingDropoff =
      currentStatus === "pending_dropoff" &&
      (supplierActionValue === "declined" ||
        (supplierActionValue === "with_supplier" &&
          selectedLocations[productId] &&
          selectedLocations[productId].length > 0));

    const confirmDisabled = locked || !(canConfirmSimple || canConfirmPendingDropoff);

    // If product is locked and not pending unlock, show "Already Linked" message
    if (locked && linkedMode !== "unlock_pending") {
      return (
        // <div className="text-center">
        //   <span className="badge bg-success-subtle text-success fs-14">Already Linked</span>
        // </div>
         <div className="text-center d-flex justify-content-center align-items-center">
                <div
                  style={{
                    display: "inline-block",
                    padding: "3px 7px",
                    borderRadius: "0.525rem",
                    fontSize: "0.75rem",
                    textAlign: "center",
                    backgroundColor: "#d2f5d2",
                    color: "#368727",
                    border: "0.2px solid",
                    borderColor: "#36872750",
                    whiteSpace: "nowrap",
                  }}
                >
                  Already Linked
                </div>
              </div>
      );
    }

    return (
      <>
        {linkedMode === "unlock_pending" ? (
          <>
            <p className="text-danger mb-1 fw-bold">Unlink Requested</p>
            <button
              className="btn btn-danger btn-sm mb-2"
              onClick={() => handleUnlink(productId, agentProduct.agent_id)}
            >
              Approve
            </button>
          </>
        ) : (
          <select
            name="supplierAction"
            className="form-control mb-2 supplier-select form-select po-select"
            value={supplierActionValue}
            onChange={(e) => handleSupplierActionChange(productId, e.target.value)}
          >
            {currentStatus === "awaiting_response" && (
              <>
                <option value="" disabled>
                  Select
                </option>
                <option value="in_transit">In Transit</option>
                <option value="declined">Decline</option>
              </>
            )}

            {currentStatus === "in_transit" && (
              <>
                <option value="" disabled>
                  Select
                </option>
                <option value="on_site">On site</option>
                <option value="declined">Decline</option>
              </>
            )}

            {currentStatus === "pending_dropoff" && (
              <>
                <option value="" disabled>
                  Select
                </option>
                <option value="with_supplier">With Supplier</option>
                <option value="declined">Decline</option>
              </>
            )}
          </select>
        )}

        {supplierActionValue === "with_supplier" && (
          <>
            {/* Textarea always shown for with_supplier */}
            <textarea
              className="form-control mb-2 "
              id={`notes-${productId}`}
              name={`notes-${productId}`}
              placeholder="Add notes (optional)..."
              value={note}
              onChange={(e) => onNoteChange(productId, e.target.value)}
            />
          </>
        )}

        {/* Show textarea if declined */}
        {supplierActionValue === "declined" && (
          <textarea
            className="form-control mb-2"
            id={`notes-${productId}`}
            name={`notes-${productId}`}
            placeholder="Add Reason..."
            value={note}
            onChange={(e) => onNoteChange(productId, e.target.value)}
          />
        )}

        <div className="text-center">
          <button
            type="button"
            className=" text-center export-button  confirm-btn"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (!confirmDisabled) {
                handleSupplierAction(productId, agentProduct.agent_id);
              }
            }}
            disabled={confirmDisabled}
            style={{ pointerEvents: confirmDisabled ? 'none' : 'auto' }}
          >
            Confirm
          </button>
        </div>
      </>
    );
  };



  // const SupplierActionsCell: React.FC<SupplierActionsCellProps> = ({
  //   agentProduct, note, binLocation,
  //   onNoteChange, onBinChange,
  //   rotationProduct, warehouseSelections,
  //   supplierActionSelections, handleRotationalProduct,
  //   handleWarehouseChange, handleSupplierActionChange,
  //   handleSupplierAction, handleUnlink, warehouses, zoneSelections, rockSelections, handleZoneChange, handleRockChange, warehouseData, binLocationSelections
  // }) => {
  //   return (
  //     <>
  //       {agentProduct?.product?.linked_mode === "unlock_pending" ? (
  //         <>
  //           <p className="text-danger mb-0 fw-bold">
  //             Unlink Requested
  //           </p>
  //           <button
  //             className="btn btn-danger btn-sm mb-2"
  //             onClick={() =>
  //               handleUnlink(
  //                 agentProduct?.product_id,
  //                 agentProduct?.agent_id
  //               )
  //             }
  //           >
  //             Approval Unilink
  //           </button>
  //         </>
  //       ) : (
  //         <select
  //           name="supplierAction"
  //           className="form-control"
  //           value={
  //             supplierActionSelections[
  //             agentProduct.product_id
  //             ] ?? ""
  //           }
  //           onChange={(e) =>
  //             handleSupplierActionChange(agentProduct.product_id, e.target.value)
  //           }
  //           disabled={
  //             agentProduct?.product?.locked_status === "locked"
  //           }
  //         >
  //           {agentProduct?.product?.current_status === "awaiting_response" ? (
  //             <>
  //               <option value="" disabled>
  //                 Select
  //               </option>
  //               <option value="in_transit">
  //                 In Transit
  //               </option>
  //               <option value="declined">
  //                 Decline
  //               </option>
  //             </>
  //           ) : agentProduct?.product?.current_status === "in_transit" ? (
  //             <>
  //               <option value="" disabled>
  //                 Select
  //               </option>
  //               {/* <option value="with_supplier">
  //                       With Supplier
  //                     </option> */}

  //               <option value="on_site">
  //                 On site
  //               </option>

  //               <option value="declined">
  //                 Decline
  //               </option>
  //             </>
  //           ) : agentProduct?.product?.current_status === "pending_dropoff" ? (
  //             <>
  //               <option value="" disabled>
  //                 Select
  //               </option>
  //               <option value="with_supplier">
  //                 With Supplier
  //               </option>
  //               <option value="declined">
  //                 Decline
  //               </option>
  //             </>
  //           ) : (
  //             <></>
  //           )}
  //         </select>
  //       )}

  //       {/* Conditionally show if "with_supplier" is selected */}
  //       {supplierActionSelections[agentProduct.product_id] === "with_supplier" && (
  //         <>
  //           {/* Select Warehouse */}
  //           <select
  //             name="warehouseOpt"
  //             className="form-control mb-2"
  //             value={
  //               warehouseSelections[agentProduct.product_id] ?? ""
  //             }
  //             onChange={(e) => {
  //               console.log('Selected warehouse:', e.target.value)
  //               handleWarehouseChange(agentProduct.product_id, e.target.value)
  //             }
  //             }
  //           >
  //             <option value="">
  //               Select Warehouse
  //             </option>
  //             {warehouses?.map((wh) => (
  //               <option key={wh._id} value={wh._id}>
  //                 {wh.label}
  //               </option>
  //             ))}
  //           </select>

  //           {/* Zone Select */}
  //           <select
  //             name="zoneSelect"
  //             className="form-control mb-2"
  //             value={zoneSelections[agentProduct.product_id] ?? ""}
  //             onChange={e => handleZoneChange(agentProduct.product_id, e.target.value)}
  //           >
  //             <option value="">Select Zone</option>
  //             {(warehouseData[agentProduct.product_id]?.zones || []).map(zone => (
  //               <option key={zone._id} value={zone._id}>{zone.label}</option>
  //             ))}
  //           </select>


  //           {/* Rock Select */}
  //           <select
  //             name="rockSelect"
  //             className="form-control mb-2"
  //             value={rockSelections[agentProduct.product_id] ?? ""}
  //             onChange={e => handleRockChange(agentProduct.product_id, e.target.value)}
  //           >
  //             <option value="">Select Rock</option>
  //             {/* Replace with your rock options */}
  //             {(warehouseData[agentProduct.product_id]?.racks || []).map(rack => (
  //               <option key={rack._id} value={rack._id}>{rack.label}</option>
  //             ))}

  //           </select>

  //           {/* Bin Location Select (replaces input) */}
  //           <select
  //             name="binLocationSelect"
  //             className="form-control mb-2"
  //             value={binLocationSelections[agentProduct.product_id] ?? ""}
  //             onChange={e => onBinChange(agentProduct.product_id, e.target.value)}
  //           >
  //             <option value="">Select Bin Location</option>
  //             {(warehouseData[agentProduct.product_id]?.bins || []).map(bin => (
  //               <option key={bin._id} value={bin._id}>{bin.label}</option>
  //             ))}
  //           </select>
  //           {/* Rotational Product Dropdown */}
  //           <select
  //             name="rotationalProduct"
  //             className="form-control mb-2"
  //             value={
  //               rotationProduct[agentProduct.product_id] ?? ""
  //             }
  //             onChange={(e) =>
  //               handleRotationalProduct(agentProduct.product_id, e.target.value)
  //             }
  //           >
  //             <option value="">
  //               Is Rotational Product?
  //             </option>
  //             <option value="Yes">Yes</option>
  //             <option value="No">No</option>
  //           </select>
  //         </>
  //       )}

  //       <textarea
  //         className="form-control"
  //         id={`notes-${agentProduct.product_id}`}
  //         name={`notes-${agentProduct.product_id}`}
  //         placeholder={
  //           ["declined", "reject"].includes(
  //             supplierActionSelections[
  //             agentProduct.product_id
  //             ]
  //           )
  //             ? "Add Reason..."
  //             : "Add notes..."
  //         }
  //         value={note}
  //         onChange={(e) => onNoteChange(agentProduct.product_id, e.target.value)
  //         }
  //         disabled={
  //           agentProduct?.product?.locked_status === "locked" && agentProduct?.product?.linked_mode !== "unlock_pending"
  //         }
  //       />
  //       <button
  //         className="accept-btn w-100 btn btn-success"
  //         onClick={() =>
  //           handleSupplierAction(
  //             agentProduct?.product_id,
  //             agentProduct?.agent_id
  //           )
  //         }
  //         disabled={
  //           agentProduct?.product?.locked_status === "locked"
  //         }
  //       >
  //         Confirm
  //       </button>
  //     </>
  //   );
  // };


  const columns = useMemo(() => {
    return [
      // columnHelper.display({
      //   id: "serial",
      //   header: () => <div className="th-text">S.No.</div>,
      //   cell: (info) => (
      //     <div className="text-sm">
      //       {pagination.pageIndex * pagination.pageSize + info.row.index + 1}
      //     </div>
      //   ),
      // }),
      columnHelper.display({
        id: "image",
        header: () => (
          <div className="th-text S-No">Image</div>
        ),
        cell: (info) => {
          const raw = (info.row.original?.product as any)?.images?.[0]?.url as string | undefined;
          let url = "";

          if (raw) {
            if (ABS_URL.test(raw)) {
              // already a full URL
              url = raw;
            } else if (isHexId(raw)) {
              // hex id -> resolve via presigned map / helper
              const map: Record<string, string> = (imageUrlMap as any) || {};
              url = map[raw] || getImageSrc(raw, map) || "";
            } else {
              // legacy relative path
              const base = (process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "");
              const path = raw.replace(/^\/+/, "");
              url = `${base}/${encodeURI(path)}`;
            }
          }

          const src = url || imageCustomSquare;

          return (
            <div className="text-sm S-No">
              <img
                src={src}
                alt="Product"
                style={{ width: 50, height: 50, objectFit: "contain", borderRadius: 4 }}
                onError={(e) => {
                  (e.currentTarget as HTMLImageElement).src = imageCustomSquare;
                }}
              />
            </div>
          );

        },
        enableGlobalFilter: false,
      }),
      columnHelper.accessor(
        (row: AgentProduct) => row.agent_name,
        {
          id: "agent_name",
          header: () => (
            <div className="th-text">
              Agent Name
            </div>
          ),
          cell: info => (
            <div className="text-sm">
              {info.getValue() || "-"}
            </div>
          ),
          filterFn: fuzzyTextFilter,
          enableGlobalFilter: true,
        }
      ),

      columnHelper.accessor(
        (row: AgentProduct) => row.agent_email,
        {
          id: "agent_email",
          header: () => (
            <div className="th-text">
              Agent Email
            </div>
          ),
          cell: info => (
            <div className="text-sm">
              {info.getValue() || "-"}
            </div>
          ),
          filterFn: fuzzyTextFilter,
          enableGlobalFilter: true,
        }
      ),
      columnHelper.accessor(
        (row: AgentProduct) => row?.product?.name,
        {
          id: "name",
          header: () => (
            <div className="th-text">Product Name</div>
          ),
          cell: (info) => (
            <div className="text-sm wrap-anywhere">{info.getValue()}</div>
          ),
          filterFn: fuzzyTextFilter,
          enableGlobalFilter: true,
        }
      ),
      columnHelper.accessor(
        (row: AgentProduct) => row.product.sku || "-",
        {
          id: "sku",
          header: () => (
            <div className="th-text">SKU</div>
          ),
          cell: (info) => (
            <div className="text-sm">{info.getValue()}</div>
          ),
          filterFn: fuzzyTextFilter,
          enableGlobalFilter: true,
        }
      ),
      columnHelper.accessor(
        (row: AgentProduct) => row.product.category || "-",
        {
          id: "category",
          header: () => (
            <div className="th-text">Category</div>
          ),
          cell: (info) => (
            <div className="text-sm">{info.getValue()}</div>
          ),
          filterFn: fuzzyTextFilter,
          enableGlobalFilter: true,
        }
      ),
      columnHelper.accessor(
        (row: AgentProduct) => row.product.variants?.[0]?.inventory?.[0]?.available || 0,
        {
          id: "qty",
          header: () => (
            <div className="th-text">Qty</div>
          ),
          cell: (info) => (
            <div className="text-sm">{info.getValue()}</div>
          ),
          filterFn: fuzzyTextFilter,
          enableGlobalFilter: true,
        }
      ),
      columnHelper.accessor(
        (row: AgentProduct) => row.product.item_condition || "-",
        {
          id: "item_condition",
          header: () => (
            <div className="th-text">Condition</div>
          ),
          cell: (info) => (
            <div className="text-sm">{info.getValue()}</div>
          ),
          filterFn: fuzzyTextFilter,
          enableGlobalFilter: true,
        }
      ),
      // columnHelper.accessor(
      //   (row: AgentProduct) => row.created_by?.name || "-",
      //   {
      //     id: "created_by",
      //     header: () => (
      //       <div className="th-text">Linked By</div>
      //     ),
      //     cell: (info) => (
      //       <div className="text-sm">{info.getValue()}</div>
      //     ),
      //     filterFn: fuzzyTextFilter,
      //     enableGlobalFilter: true,
      //   }
      // ),
      columnHelper.accessor(
        (row: AgentProduct) => row?.product?.product_location_status?.replace(/_/g, " "),
        {
          id: "product_location_status",
          header: () => (
            <div className="th-text">Handover Method</div>
          ),
          cell: (info) => (
            <div className="text-sm">{info.getValue()}</div>
          ),
          filterFn: fuzzyTextFilter,
          enableGlobalFilter: true,
        }
      ),

      columnHelper.accessor(
        (row: AgentProduct) => { return row?.product?.current_status?.replace(/_/g, " ") },
        {
          id: "current_status",
          header: () => (
            <div className="th-text">Product location status</div>
          ),
          cell: (info) => (
            <div className="text-sm">
              <StatusBadge status={info.getValue()} />
            </div>
          ),
          filterFn: fuzzyTextFilter,
          enableGlobalFilter: true,
        }
      ),
      columnHelper.accessor(
        (row: AgentProduct) => row?.product?.locked_status === "unlocked" ? "" : "Linked",
        {
          id: "locked_status",
          header: () => (
            <div className="th-text">Linked Status</div>
          ),
          cell: (info) => (
            <div className="text-sm">{info.getValue()}</div>
          ),
          filterFn: fuzzyTextFilter,
          enableGlobalFilter: true,
        }
      ),
      columnHelper.display({
        id: "supplierActions",
        header: () => (
          <div className="th-text">Supplier Actions</div>
        ),
        cell: ({ row, table }) => {
          const product = row.original;
          const m = table.options.meta as MyTableMeta;

          return (
            <div className="text-sm">
              <SupplierActionsCell
                key={product.product_id}
                agentProduct={product}
                note={m.noteSelections[product.product_id] ?? ""}
                binLocation={m.binLocationSelections[product.product_id] ?? ""}
                onNoteChange={m.handleNoteChange}
                onBinChange={m.handleBinLocationChange}
                rotationProduct={m.rotationProduct}
                warehouseSelections={m.warehouseSelections}
                supplierActionSelections={m.supplierActionSelections}
                handleRotationalProduct={m.handleRotationalProduct}
                handleWarehouseChange={m.handleWarehouseChange}
                handleSupplierActionChange={m.handleSupplierActionChange}
                handleSupplierAction={m.handleSupplierAction}
                handleUnlink={m.handleUnlink}
                warehouses={m.warehouses}
                zoneSelections={m.zoneSelections}
                rockSelections={m.rockSelections}
                binLocationSelections={m.binLocationSelections}
                handleZoneChange={m.handleZoneChange}
                handleRockChange={m.handleRockChange}
                warehouseData={m.warehouseData}
                selectedLocations={m.selectedLocations}
              />
            </div>
          );
        },
        enableGlobalFilter: false,
      }),
      columnHelper.display({
        id: "actions",
        header: () => (
          <div className="th-text">Actions</div>
        ),
        cell: (info) => {
          const productInfo = info.row.original;
          return (
            <div className="d-flex align-items-center gap-2">
              {canView && (
              <ViewIconButton
                to={`${baseRoute}/viewAgentProduct/${productInfo.agent_id}/${productInfo.product_id}`} className="btn action-btn " title="View" />
              )}
              {/* <Link to={`${baseRoute}/viewAgentProduct/${productInfo.agent_id}/${productInfo.product_id}`} className="btn action-btn " title="View">
                <i className="ri-eye-fill fs-18" />
              </Link> */}
              {/* <Link
                to={`${baseRoute}/editIncomingProduct/${productInfo.agent_id}/${productInfo.product_id}`}
                className="btn btn-warning btn-sm"
              >
                Edit
              </Link> */}
            </div>
          );
        },
      }),
    ];
  }, [pagination, imageUrlMap, canView]);



  // const columns = useMemo(() => {
  //   return [
  //     columnHelper.display({
  //       id: "serial",
  //       header: "S.No.",
  //       cell: (info) => pagination.pageIndex * pagination.pageSize + info.row.index + 1,
  //     }),
  //     columnHelper.display({
  //       id: "image",
  //       header: "Image",
  //       cell: (info) => {
  //         const url = `${process.env.REACT_APP_BASE_URL}${info.row.original?.product?.image?.url}`;
  //         return info.row.original?.product?.image?.url ? (
  //           <img src={url} alt="Product" style={{ width: 40, height: 40, objectFit: "cover", borderRadius: 4 }} />
  //         ) : (
  //           <span>No Image</span>
  //         );
  //       },
  //       enableGlobalFilter: false,
  //     }),
  //     columnHelper.accessor(
  //       (row: AgentProduct) => row?.product?.name,
  //       {
  //         header: "Product Name",
  //         filterFn: fuzzyTextFilter,
  //         enableGlobalFilter: true,
  //       }),
  //     columnHelper.accessor(
  //       (row: AgentProduct) => row.product.sku || "-",
  //       {
  //         header: "SKU",
  //         filterFn: fuzzyTextFilter,
  //         enableGlobalFilter: true,
  //       }),
  //     columnHelper.accessor(
  //       (row: AgentProduct) => row.product.category || "-",
  //       {
  //         header: "Category",
  //         filterFn: fuzzyTextFilter,
  //         enableGlobalFilter: true,
  //       }),
  //     columnHelper.accessor(
  //       (row: AgentProduct) => row.product.variants?.[0]?.inventory?.[0]?.available || 0,
  //       {
  //         header: "Qty",
  //         filterFn: fuzzyTextFilter,
  //         enableGlobalFilter: true,
  //       }),
  //     columnHelper.accessor(
  //       (row: AgentProduct) => row.product.item_condition || "-",
  //       {
  //         header: "Condition",
  //         filterFn: fuzzyTextFilter,
  //         enableGlobalFilter: true,
  //       }),
  //     columnHelper.accessor(
  //       (row: AgentProduct) => row.created_by?.name || "-",
  //       {
  //         header: "Linked By",
  //         filterFn: fuzzyTextFilter,
  //         enableGlobalFilter: true,
  //       }),
  //     columnHelper.accessor(
  //       (row: AgentProduct) => row?.product?.product_location_status?.replace(/_/g, " "),
  //       {
  //         header: "Product Location Status",
  //         filterFn: fuzzyTextFilter,
  //         enableGlobalFilter: true,
  //       }),
  //     columnHelper.display({
  //       id: "supplierActions",
  //       header: "Supplier Actions",
  //       cell: ({ row, table }) => {
  //         const product = row.original;
  //         const m = (table.options.meta as MyTableMeta);

  //         return (
  //           <SupplierActionsCell
  //             key={product.product_id} // stable key to prevent remount
  //             agentProduct={product}
  //             note={m.noteSelections[product.product_id] ?? ""}
  //             binLocation={m.binLocationSelections[product.product_id] ?? ""}
  //             onNoteChange={m.handleNoteChange}
  //             onBinChange={m.handleBinLocationChange}
  //             rotationProduct={m.rotationProduct}
  //             warehouseSelections={m.warehouseSelections}
  //             supplierActionSelections={m.supplierActionSelections}
  //             handleRotationalProduct={m.handleRotationalProduct}
  //             handleWarehouseChange={m.handleWarehouseChange}
  //             handleSupplierActionChange={m.handleSupplierActionChange}
  //             handleSupplierAction={m.handleSupplierAction}
  //             handleUnlink={m.handleUnlink}
  //             warehouses={m.warehouses}
  //             zoneSelections={m.zoneSelections}
  //             rockSelections={m.rockSelections}
  //             binLocationSelections={m.binLocationSelections}
  //             handleZoneChange={m.handleZoneChange}
  //             handleRockChange={m.handleRockChange}
  //             warehouseData={m.warehouseData}
  //           />
  //         );
  //       },
  //       enableGlobalFilter: false,
  //     }),

  //     columnHelper.accessor(
  //       (row: AgentProduct) => { return row?.product?.current_status?.replace(/_/g, " ") },
  //       {
  //         header: "Current Status",
  //         filterFn: fuzzyTextFilter,
  //         enableGlobalFilter: true,
  //       }),
  //     columnHelper.accessor(
  //       (row: AgentProduct) => row?.product?.locked_status === "unlocked" ? "" : "Linked",
  //       {
  //         header: "Linked Status",
  //         filterFn: fuzzyTextFilter,
  //         enableGlobalFilter: true,
  //       }),
  //     columnHelper.display({
  //       id: "actions",
  //       header: "Actions",
  //       cell: (info) => {
  //         const productInfo = info.row.original;
  //         return (
  //           <div className="d-flex gap-2">
  //             <Link to={`${baseRoute}/viewAgentProduct/${productInfo.agent_id}/${productInfo.product_id}`}><i className="ri-eye-fill text-primary"></i></Link>
  //             {/* <Link
  //                 to={`${baseRoute}/editIncomingProduct/${productInfo.agent_id}/${productInfo.product_id}`}
  //                 className="btn btn-warning btn-sm"
  //               >
  //                 Edit
  //               </Link> */}
  //           </div>
  //         );
  //       },
  //     }),
  //   ];
  // }, [
  //   // deleting,
  //   // supplierActionSelections,
  //   // warehouseSelections,
  //   // binLocationSelections,
  //   // rotationProduct,
  //   // noteSelections,
  //   // handleSupplierActionChange,
  //   // handleWarehouseChange,
  //   // handleBinLocationChange,
  //   // handleNoteChange,
  //   // handleRotationalProduct,
  //   // handleSupplierAction,
  //   // handleUnlink,
  //   // linkedStatusFilter,
  //   // currentStatusFilter
  // ]);


  const table = useReactTable<AgentProduct>({
    data: agentProducts || [],
    columns,
    pageCount: Math.ceil(rowCount / pagination.pageSize),
    state: {
      pagination,
      globalFilter,
    },
    manualPagination: true,
    manualFiltering: true,
    onPaginationChange: setPagination,
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    meta: {
      // deleting,
      // supplierActionSelections,
      // warehouseSelections,
      // binLocationSelections,
      // rotationProduct,
      // noteSelections,
      // handleSupplierActionChange,
      // handleWarehouseChange,
      // handleBinLocationChange,
      // handleNoteChange,
      // handleRotationalProduct,
      // handleSupplierAction,
      // handleUnlink,
      // linkedStatusFilter,
      // currentStatusFilter
      noteSelections,
      binLocationSelections,
      rotationProduct,
      warehouseSelections,
      supplierActionSelections,
      handleNoteChange,
      handleBinLocationChange,
      handleRotationalProduct,
      handleWarehouseChange,
      handleSupplierActionChange,
      handleSupplierAction,
      handleUnlink,
      warehouses,
      zoneSelections,
      rockSelections,
      handleZoneChange,
      handleRockChange,
      warehouseData,
      selectedLocations,
    },
  });

  // Add this helper so TS knows about your meta
  type TableType = typeof table & { options: { meta: MyTableMeta } };



  const pageCount = table.getPageCount();
  const currentPage = table.getState().pagination.pageIndex;

  return (
    <div className="content" ref={topRef}>
      <div className="po-header mb-4 shadow-border-datatables">
        {/* TOP ROW */}
        <div className="po-top d-flex justify-content-between align-items-start align-items-md-center flex-wrap gap-2">
          <div className="po-title-wrap me-2">
            <h4 className="mb-1 po-title">Incoming Agent Products</h4>
            <div className="po-subtitle">Manage incoming products from suppliers and brokerages</div>
          </div>

          {/* tools block (search + buttons) */}
          <div className="po-tools d-flex align-items-center flex-wrap gap-2 ms-auto">
            {/* Search */}
            <div className="input-group po-search ">
              <span className="input-group-text">
                <i className="ri-search-line" />
              </span>
              <input
                type="text"
                id="searchInput"
                name="searchInput"
                className="form-control"
                placeholder="Search by Product Name, SKU, Brokerages…"
              // value={searchInput}
              // onChange={(e) => setSearchInput(e.target.value)}
              // onKeyDown={(e) => { if (e.key === "Enter") setGlobalFilter(searchInput); }}
              autoComplete="off"
              />
            </div>

            {/* Filters toggle */}
            <button
              type="button"
              className={` po-filter-btn d-inline-flex align-items-center ${filtersOpen ? "active" : ""}`}
              onClick={() => setFiltersOpen(v => !v)}
              aria-expanded={filtersOpen}
              aria-controls="incoming-filters"
            >
              <i className="ri-filter-3-line me-2" />
              <span>Filters</span>
              <i className={`ri-arrow-down-s-line ms-1 ${filtersOpen ? "rotate-180" : ""}`} />
            </button>

            {/* Export */}
            <button className="export-button align-items-center">
              <i className="ri-download-2-line me-2" />
              Export to Excel
            </button>
          </div>
        </div>

        <div id="incoming-filters" className={`po-filters ${filtersOpen ? "show" : ""}`}>
          <div className="po-filters-bar d-flex"> {/* <-- flex wrapper */}
            <div className="row g-2 g-md-3 align-items-end justify-content-md-end ms-md-auto w-100 w-md-auto">
              <div className="col-12 col-md-auto">
                <label htmlFor="linkedStatusFilter" className="form-label mb-1 label-1">Linked Status</label>
                <select
                  id="linkedStatusFilter"
                  className="form-select"
                  value={tempLinkedStatus}
                  onChange={(e) => setTempLinkedStatus(e.target.value)}
                >
                  <option value="all">All</option>
                  <option value="accepted">Linked</option>
                  <option value="unlock_pending">Unlinked</option>
                </select>
              </div>

              <div className="col-12 col-md-auto">
                <label htmlFor="currentStatusFilter" className="form-label mb-1 label-1">Current Status</label>
                <select
                  id="currentStatusFilter"
                  className="form-select"
                  value={tempCurrentStatus}
                  onChange={(e) => setTempCurrentStatus(e.target.value)}
                >
                  <option value="all">All</option>
                  <option value="With Supplier">With Supplier</option>
                  <option value="In Warehouse">In Warehouse</option>
                </select>
              </div>

              <div className="col-12 col-md-auto d-flex justify-content-md-end gap-2 mt-1 mt-md-0">
                <button
                  className="btn btn-primary table-addbutton flex-fill flex-md-grow-0"
                  onClick={() => {
                    setLinkedStatusFilter(tempLinkedStatus);
                    setCurrentStatusFilter(tempCurrentStatus);
                  }}
                >
                  <i className="ri-check-line me-1" />
                  Apply Filter
                </button>

                <button
                  className="btn back-btn surface-btn fw-bold fs-16 flex-fill flex-md-grow-0"
                  onClick={() => {
                    setTempLinkedStatus("all");
                    setTempCurrentStatus("all");
                    setLinkedStatusFilter("all");
                    setCurrentStatusFilter("all");
                  }}
                >
                  Clear
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* <div className="d-flex  justify-content-between align-items-center border-bottom pb-3 mb-4 page-container">
        <h4 className="mb-0 fw-bold">Incoming Agent Products</h4>

        <div className="d-flex gap-3 align-items-center">
          <div className="input-group" style={{ width: "230px", height: "40px" }}>
            <span className="input-group-text bg-white border-end-0 p-2 left-radius">
              <i className="ri-search-line fs-15"></i>
            </span>
            <input
              type="text"
              id="searchInput"
              name="searchInput"
              className="form-control border-start-0 ps-0 right-radius text-ellipsis"
              // placeholder="Search by Agent Name, Product Name or SKU"
              // value={searchInput}
              // onChange={(e: ChangeEvent<HTMLInputElement>) => setSearchInput(e.target.value)}
              // onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => {
              //   if (e.key === "Enter") {
              //     setGlobalFilter(searchInput);
              //   }
              // }}
              style={{ borderColor: "#cccccc" }}
            />

          </div>
        </div>
      </div> */}

      {/* Search and Export Buttons */}
      {/* <div className="row mb-3 justify-content-between">
        <div className="col-md-4 d-flex gap-2">
          <select
            name="linkedStatusFilter"
            id="selectionincomingagentproduct"

            className="form-select"
            value={tempLinkedStatus}
            onChange={(e) => setTempLinkedStatus(e.target.value)}
          >
            <option value="all">Linked Status</option>
            <option value="accepted">Linked</option>
            <option value="unlock_pending">Unlinked</option>
          </select>

          <select
            name="currentStatusFilter"
            className="form-select"
            id="selectionincomingagentproduct"

            value={tempCurrentStatus}
            onChange={(e) => setTempCurrentStatus(e.target.value)}
          >
            <option value="all">Current Status</option>
            <option value="with_supplier">With Supplier</option>
            <option value="on_site">On Site</option>
          </select>

          <button
            className="btn btn-primary w-25"
            onClick={() => {
              setLinkedStatusFilter(tempLinkedStatus);
              setCurrentStatusFilter(tempCurrentStatus);
            }}
          >
            Apply Filter
          </button>
        </div>

        <div className="col-md-2"></div>
      </div> */}
      <div className="card overflow-hidden production-card box-shadow-datatables shadow-border-datatables">
        <div className="align-items-center bg-white border-bottom d-flex justify-content-between px-4 py-4">
          <div className="d-flex align-items-center">
            <i
              className="bi bi-box-seam me-2"
              aria-hidden="true"
              style={{ fontSize: 18, color: "#2563eb", lineHeight: 1 }}
            />
            <h5 className="mb-0">Incoming Products</h5>
          </div>

          {/* <span className="badge rounded-pill bg-body-tertiary text-secondary-emphasis px-3 py-2">
            orders
          </span> */}
        </div>
        <hr className="mb-2" style={{ borderTop: "none" }} />


        {/* DESKTOP/TABLET */}
        <div className="rdt-desktop">
          <div
            className="production-scroll"
            style={{ "--rdt-min": "1200px" } as React.CSSProperties}
          >
            <table className="table production-table align-middle mb-0 table-1">
              {loading ? (
                <DesktopTableLoader table={table} message="Loading incoming agent products…" />
              ) : (
                <DesktopTableParts table={table} />
              )}
            </table>
          </div>
        </div>

        {/* MOBILE (≤ 1021px) */}
        <div className="rdt-mobile">
          {loading ? (
            <MobileCardsLoader message="Loading incoming agent products…" />
          ) : (
            <MobileCardsOnly
            table={table}
            idColId="order_link"
            titleColId="product_name"
            actionColId="actions"
          />
          )}
        </div>

      </div>
      <DataTableStatus
        loading={loading}
        isEmpty={table.getRowModel().rows.length === 0}
      >
        <div className="mt-4 mb-4">
          <TablePaginationControls table={table} />
        </div>
      </DataTableStatus>

      {/* Bin Location Popups */}
      {(agentProducts || []).map((product) => (
        <BinLocationPopUp
          key={product.product_id}
          show={showBinLocationPopup[product.product_id] || false}
          onHide={() => {
            setShowBinLocationPopup(prev => ({ ...prev, [product.product_id]: false }));
            // If user closed popup without confirming (clicking Save), clear "With Supplier" selection
            if (!confirmedProductsRef.current.has(product.product_id)) {
              setSupplierActionSelections(prev => ({ ...prev, [product.product_id]: "" }));
            }
            // Reset the confirmed flag for this product when popup closes
            confirmedProductsRef.current.delete(product.product_id);
          }}
          onConfirm={(locations, totalQty) => {
            setSelectedLocations(prev => ({ ...prev, [product.product_id]: locations }));
            // Mark this product as confirmed in this session
            confirmedProductsRef.current.add(product.product_id);
            // Keep "With Supplier" selected if any quantity is allocated and saved
            if (locations && locations.length > 0 && totalQty > 0) {
              // Keep "With Supplier" selected - user has allocated and saved
            } else {
              // Clear "With Supplier" if no locations or no quantity allocated
              setSupplierActionSelections(prev => ({ ...prev, [product.product_id]: "" }));
            }
          }}
          productQuantity={product?.product.variants?.[0]?.inventory?.[0]?.available || 0}
          ownerNodeId={(isSignageTeammember && parentSignageId ? parentSignageId : userId) || ""}
        />
      ))}
    </div>
  );
};

export default SignageIncomingAgentProducts;
